import React, { useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { COLORS } from '../../styles';
import Portal from '../Portal';

interface Props {
  message: string;
  children: React.ReactNode;
}

interface Offsets {
  x: number;
  y: number;
}

export default function Tooltip({ message, children }: Props) {
  const trigger = useRef<HTMLDivElement>(null);
  const [isVisible, setVisible] = useState(false);
  const [offsets, setOffsets] = useState<Offsets>({ x: 0, y: 0 });

  function getOffsets(rect: DOMRect): Offsets {
    const { x, y, width, height } = rect;
    return { x: x + width + 15, y: y + height / 2 };
  }

  function show() {
    setVisible(true);
    if (!trigger.current) { return; }
    setOffsets(getOffsets(trigger.current.getBoundingClientRect()));
  }

  function hide() {
    setVisible(false);
  }

  return (
    <>
      <div
        ref={trigger}
        style={{ display: 'inline-block' }}
        onFocus={show}
        onBlur={hide}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        { children }
      </div>
      { isVisible && (
        <Portal>
          <Message {...offsets}>{ message }</Message>
        </Portal>
      ) }
    </>
  );
}

const show = keyframes`
  from {
    opacity: 0;
    transform: translate3d(-5px, -50%, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, -50%, 0);
  }
`;

const Message = styled.div<Offsets>`
  position: fixed;
  top: ${({ y }) => y}px;
  left: ${({ x }) => x}px;
  max-width: 200px;
  padding: 4px 6px;
  color: ${COLORS.gray0};
  font-size: 11px;
  font-weight: 500;
  border-radius: 3px;
  background: ${COLORS.slategray90};
  transform: translateY(-50%);
  pointer-events: none;
  animation: ${show} 0.3s ease-in-out;
`;
